import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  IconButton,
} from '@mui/material';
import { Delete, Close } from '@mui/icons-material';
import { ButtonPrimary } from '../../ui/ButtonPrimary';
import { obterBico, deletarBico } from '../offline/api';

export function BicoDetalhesModal({ open, onClose, afericaoId, numeroBico, onDeleted, afericaoFinalizada = false }) {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [detalhes, setDetalhes] = useState(null);

  useEffect(() => {
    if (open && afericaoId && numeroBico) {
      loadDetalhes();
    } else {
      setDetalhes(null);
      setError(null);
    }
  }, [open, afericaoId, numeroBico]);

  const loadDetalhes = async () => {
    setLoading(true);
    setError(null);
    try {
      const dados = await obterBico(afericaoId, numeroBico);
      setDetalhes(dados);
    } catch (err) {
      console.error('Erro ao carregar detalhes do bico:', err);
      setError(err.response?.data?.detail || err.message || 'Erro ao carregar detalhes do bico');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta aferição de bico?')) {
      return;
    }

    setDeleting(true);
    setError(null);
    try {
      await deletarBico(afericaoId, numeroBico);
      if (onDeleted) {
        onDeleted();
      }
      onClose();
    } catch (err) {
      console.error('Erro ao deletar bico:', err);
      setError(err.response?.data?.detail || err.message || 'Erro ao deletar aferição de bico');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '14px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          pr: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Detalhes do Bico
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && detalhes && (
          <Stack spacing={1.5}>
            {/* Nome do Bico */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Nome do Bico
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {detalhes.nomeBico || detalhes.NomeBico || `B${String(detalhes.numero || detalhes.Numero || numeroBico).padStart(2, '0')}`}
              </Typography>
            </Box>

            <Divider />

            {/* Tipo de Combustível */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Tipo de Combustível
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {detalhes.nomeTipoCombustivel || detalhes.NomeTipoCombustivel || 'N/A'}
              </Typography>
            </Box>

            <Divider />

            {/* Valor */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Valor
              </Typography>
              <Typography variant="body1" fontWeight={500} sx={{ fontSize: '1rem' }}>
                {detalhes.valor !== null && detalhes.valor !== undefined
                  ? `${detalhes.valor >= 0 ? '+' : ''}${detalhes.valor} ml`
                  : detalhes.Valor !== null && detalhes.Valor !== undefined
                  ? `${detalhes.Valor >= 0 ? '+' : ''}${detalhes.Valor} ml`
                  : 'N/A'}
              </Typography>
            </Box>

            <Divider />

            {/* Variação */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Variação
              </Typography>
              <Typography variant="body1" fontWeight={500} sx={{ fontSize: '1rem' }}>
                {detalhes.variacao !== null && detalhes.variacao !== undefined
                  ? `${detalhes.variacao >= 0 ? '+' : ''}${detalhes.variacao} ml`
                  : detalhes.Variacao !== null && detalhes.Variacao !== undefined
                  ? `${detalhes.Variacao >= 0 ? '+' : ''}${detalhes.Variacao} ml`
                  : 'N/A'}
              </Typography>
            </Box>

            {/* Mensagem */}
            {detalhes.mensagem || detalhes.Mensagem ? (
              <>
                <Divider />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" color="text.primary">
                    {detalhes.mensagem || detalhes.Mensagem}
                  </Typography>
                </Box>
              </>
            ) : null}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
        }}
      >
        {!afericaoFinalizada && (
          <ButtonPrimary
            onClick={handleDelete}
            disabled={deleting || loading}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            fullWidth
            sx={{
              py: 1.25,
              bgcolor: 'error.main',
              '&:hover': {
                bgcolor: 'error.dark',
              },
              '&:disabled': {
                bgcolor: 'error.light',
              },
            }}
          >
            {deleting ? 'Excluindo...' : 'Excluir Aferição'}
          </ButtonPrimary>
        )}
      </DialogActions>
    </Dialog>
  );
}

